import { DetailedHTMLProps, FC, HTMLAttributes, useMemo } from 'react';
import styles from './CircularProgress.module.scss';
import { VariantProps, cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const progressVariants = cva(['inline-flex flex-col justify-center items-center align-top'], {
    variants: {
        variant: {
            indeterminate: [styles['progress-indeterminate']],
            determinate: [styles['progress-determinate']],
        },
        color: {
            inherit: ['text-inherit'],
            primary: ['text-primary'],
            secondary: ['text-secondary'],
            success: ['text-success'],
            danger: ['text-danger'],
            warning: ['text-warning'],
        },
    },
    defaultVariants: {
        variant: 'indeterminate',
        color: 'inherit',
    },
});

const CIRCUMFERENCE = 2 * Math.PI * 20.2;

interface CircularProgressProps
    extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>, 'color'>,
        VariantProps<typeof progressVariants> {
    size?: number | string;
    progress?: number;
    showLabel?: boolean;
    label?: string;
    labelClass?: string;
}

const CircularProgress: FC<CircularProgressProps> = (props) => {
    const {
        size = 48,
        progress = 0,
        showLabel = false,
        labelClass = 'text-sm text-grayscale-900 font-medium mt-6',
        variant = 'indeterminate',
        label,
        color,
        className,
        ...rest
    } = props;

    const offset = useMemo(() => {
        let _progress = Math.max(0, progress);
        _progress = Math.min(_progress, 100);

        return CIRCUMFERENCE - (CIRCUMFERENCE * _progress) / 100;
    }, [progress]);

    return (
        <span role="progressbar" {...rest} className={twMerge(progressVariants({ variant, color, className }))}>
            <svg viewBox="22 22 44 44" width={size} height={size} className={styles['progress-svg']}>
                {variant === 'determinate' && (
                    <circle
                        className={['text-grayscale-300', styles['progress-circle']].join(' ')}
                        cx={44}
                        cy={44}
                        r={20}
                        fill="none"
                        strokeWidth={4}
                        stroke="currentColor"
                        strokeDasharray={CIRCUMFERENCE}
                        strokeDashoffset={offset - CIRCUMFERENCE}
                    />
                )}
                <circle
                    className={styles['progress-circle']}
                    cx={44}
                    cy={44}
                    r={20}
                    fill="none"
                    strokeWidth={4}
                    stroke="currentColor"
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={offset}
                />
            </svg>

            {showLabel && variant === 'determinate' && (
                <span className={labelClass}>
                    {label} {progress}%
                </span>
            )}
        </span>
    );
};

export default CircularProgress;
